/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import React, { useCallback, useState } from "react";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { TokenRowDisabledReason } from "utils/tokenSelectorUtils";
import { TOKEN_ICONS } from "../../../../config/tokenIcons";
import { Token } from "../../../../store/global/TokenEnum";
import { TokenMetadata } from "../../../../store/global/getTokenMetadata";
import { Amount } from "../../../../types/amount";
import { AddTokenButton, AddTokenButtonState } from "./AddTokenButton";
import { Text } from "../../../general/Text";
import { SelectTokenRowTooltip } from "./SelectTokenRowTooltip";
import "./TokenRow.css";
import { Counterparty, TokenSelectMode } from "./TokenSelectModal";
import { FlexBetweenContainer } from "../../../../containers/FlexBetweenContainer";
import { FlexContainer } from "../../../../containers/FlexContainer";

export interface TokenRowProps {
  symbol: Token;
  name: string;
  tokenSelectMode: TokenSelectMode;
  tokenMetaData: TokenMetadata | undefined;
  balance: Amount;
  tokenIcon: string;
  price: Amount;
  onTokenSelect: (symbol: Token) => void;
  isSelected: boolean;
  isDisabled: boolean;
  isUnavailable: boolean;
  selectedTokens: Array<Token>;
  counterParty: Counterparty;
  tokenDisabledReason: TokenRowDisabledReason | null;
}

export const TokenRow: React.FC<TokenRowProps> = ({
  tokenSelectMode,
  tokenMetaData,
  symbol,
  name,
  balance,
  tokenIcon,
  price,
  onTokenSelect,
  isSelected,
  isDisabled,
  isUnavailable,
  selectedTokens,
  counterParty,
  tokenDisabledReason,
}) => {
  const [isHovered, setIsHovered] = useState<boolean>(false);
  const [imageLoaded, setImageLoaded] = useState<boolean>(false);
  const addTokenButtonState = getAddTokenButtonState({
    isSelected,
    isDisabled,
    isHovered,
    isUnavailable,
  });
  const displayBalance = getDisplayBalance(balance, symbol, tokenMetaData);
  const totalPrice = new Amount(price.value.mul(balance.value).toString());
  const dataTestId = getDataTestId(symbol, addTokenButtonState);
  const handleTokenSelect = useCallback(() => {
    if (isDisabled || isUnavailable) {
      return;
    }
    onTokenSelect(symbol);
  }, [isDisabled, isUnavailable, onTokenSelect, symbol]);

  return (
    <SelectTokenRowTooltip
      isDisabled={isDisabled}
      tokenDisabledReason={tokenDisabledReason}
      counterParty={counterParty}
      selectedTokens={selectedTokens}
      symbol={symbol}
      canTokenBeTraded={!isUnavailable}
    >
      <TokenRowContainer
        key={TOKEN_ICONS[symbol]}
        isDisabled={isDisabled || isUnavailable}
        data-testid={dataTestId}
        onClick={handleTokenSelect}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <FlexContainer>
          <div
            css={css`
              margin-right: 14px;
            `}
          >
            <img
              width="32"
              height="32"
              style={imageLoaded ? {} : { display: "none" }}
              src={tokenIcon}
              alt={symbol}
              onLoad={() => setImageLoaded(true)}
            />
            <Skeleton
              style={!imageLoaded ? {} : { display: "none" }}
              width={32}
              height={32}
              baseColor={"#334545"}
              highlightColor={"#4D5D5DB3"}
              borderRadius={16}
            />
          </div>
          <FlexBetweenContainer direction="row" css={{ width: "100%" }}>
            <div>
              <Text textStyle="Title" size="Small" data-testid="token-row-symbol-text">
                {symbol}
              </Text>
              <Text
                textStyle="Paragraph"
                size="Large"
                color="#9EAFB1"
                textCss={css`
                  position: relative;
                  display: flex;
                  align-items: center;
                  gap: 6px;
                `}
              >
                {name}
              </Text>
            </div>
            <div style={{ textAlign: "right" }}>
              <Text textStyle="Title" size="Small" data-testid="token-row-symbol-text">
                {displayBalance}
              </Text>
              <Text textStyle="Paragraph" size="Large" color="#808B8B">
                {totalPrice.value.gt(0) ? `$${totalPrice.format(2)}` : ""}
              </Text>
            </div>
          </FlexBetweenContainer>
          <TokenSelectIconContainer>
            {tokenSelectMode === "multiple" && addTokenButtonState != null ? (
              <AddTokenButton state={addTokenButtonState} />
            ) : null}
          </TokenSelectIconContainer>
        </FlexContainer>
      </TokenRowContainer>
    </SelectTokenRowTooltip>
  );
};

// Sub component

export const TokenRowContainer = styled.div<{ isDisabled: boolean }>`
  border-bottom: 1px solid #21373a;
  padding: 0 20px;
  width: 100%;
  height: 69px;
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  cursor: ${props => (props.isDisabled ? "not-allowed" : "pointer")};
  opacity: ${props => (props.isDisabled ? ".5" : "1")};
  --bs-gutter-y: 0;

  :last-child {
    border-bottom: none;
  }

  :hover {
    background: #33454566;
  }

  > * {
    flex-shrink: 0;
    width: 100%;
    max-width: 100%;
    padding-right: calc(var(--bs-gutter-x) * 0.5);
    padding-left: calc(var(--bs-gutter-x) * 0.5);
    margin-top: var(--bs-gutter-y);
  }
`;

export const TokenSelectIconContainer = styled.div`
  margin-left: 14px;
  display: flex;
  align-items: center;
`;
// Helpers

interface getAddTokenButtonStateArgs {
  isSelected: boolean;
  isDisabled: boolean;
  isHovered: boolean;
  isUnavailable: boolean;
}

function getAddTokenButtonState({
  isSelected,
  isDisabled,
  isHovered,
  isUnavailable,
}: getAddTokenButtonStateArgs) {
  if (isUnavailable) return AddTokenButtonState.unavailable;
  if (isDisabled) return AddTokenButtonState.disabled;
  if (isSelected) return AddTokenButtonState.selected;
  if (isHovered) return AddTokenButtonState.hover;
  return AddTokenButtonState.default;
}

function getDisplayBalance(
  balance: Amount,
  symbol: Token,
  tokenMetaData: TokenMetadata | undefined
) {
  if (balance.value.eq("0")) {
    return "";
  }
  return balance.toDisplay(symbol, tokenMetaData);
}

function getDataTestId(symbol: Token, addTokenButtonState: AddTokenButtonState | null) {
  if (addTokenButtonState === null) return `${symbol}-token-row-unavailable`;
  if (addTokenButtonState === AddTokenButtonState.selected) return `${symbol}-token-row-selected`;
  if (addTokenButtonState === AddTokenButtonState.disabled) return `${symbol}-token-row-disabled`;
  return `${symbol}-token-row`;
}
